import { useState, useEffect, useRef } from 'react';

const differentials = [
  {
    id: 'acolhimento',
    number: '01',
    title: 'Atendimento Humanizado',
    desc: 'Cada consulta é um momento de escuta, respeito e dedicação exclusiva. Você nunca será apenas mais um prontuário.',
  },
  {
    id: 'tecnologia',
    number: '02',
    title: 'Tecnologia Avançada',
    desc: 'Equipamentos de última geração para diagnósticos precisos e tratamentos minimamente invasivos com máximo conforto.',
  },
  {
    id: 'individualidade',
    number: '03',
    title: 'Cuidado Personalizado',
    desc: 'Cada mulher é única. Seu plano de cuidado é desenhado sob medida para a sua fase de vida e necessidades específicas.',
  },
  {
    id: 'sigilo',
    number: '04',
    title: 'Privacidade Absoluta',
    desc: 'Ambiente reservado e seguro, onde você pode se sentir à vontade para compartilhar suas questões mais íntimas.',
  },
  {
    id: 'formacao',
    number: '05',
    title: 'Formação Continuada',
    desc: 'Atualização constante com os principais centros de referência em saúde feminina do Brasil e do exterior.',
  },
  {
    id: 'ambiente',
    number: '06',
    title: 'Ambiente Acolhedor',
    desc: 'Clínica projetada para que você se sinta em um espaço de cuidado, beleza e tranquilidade desde a recepção.',
  },
];

export default function DifferentialsSection() {
  const [visibleCards, setVisibleCards] = useState<boolean[]>(
    new Array(differentials.length).fill(false)
  );
  const [headerVisible, setHeaderVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = cardsRef.current.indexOf(
              entry.target as HTMLDivElement
            );
            if (index !== -1) {
              setTimeout(() => {
                setVisibleCards((prev) => {
                  const next = [...prev];
                  next[index] = true;
                  return next;
                });
              }, index * 120);
            }
            if (entry.target === sectionRef.current) {
              setHeaderVisible(true);
            }
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -60px 0px' }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    cardsRef.current.forEach((card) => {
      if (card) observer.observe(card);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      data-cascade
      className="relative w-full py-28 md:py-40 bg-offwhite paper-texture overflow-hidden"
    >
      {/* Subtle ambient light — consistent with rest of site */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[10%] right-[5%] w-[30%] h-[30%] rounded-full bg-rose-light/5 blur-[140px]" />
        <div className="absolute bottom-[10%] left-[8%] w-[25%] h-[25%] rounded-full bg-sage-light/4 blur-[100px]" />
      </div>

      <div className="relative max-w-[1100px] mx-auto px-6 md:px-12">
        {/* Editorial Header */}
        <div className="text-center mb-16 md:mb-24">
          <p
            data-cascade-label
            className={`font-body text-[10px] md:text-[11px] text-secondary-500 tracking-[0.4em] uppercase mb-4 transition-all duration-1000 ease-out ${
              headerVisible
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-4'
            }`}
          >
            Diferenciais
          </p>
          <div
            data-cascade-line
            className={`flex items-center justify-center gap-4 mb-5 transition-all duration-1000 delay-200 ease-out ${
              headerVisible
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-4'
            }`}
          >
            <div className="w-8 md:w-12 h-[1px] bg-gold-matte/30" />
            <div className="w-1.5 h-1.5 rounded-full bg-gold-matte/40" />
            <div className="w-8 md:w-12 h-[1px] bg-gold-matte/30" />
          </div>
          <h2
            data-cascade-title
            className={`font-heading text-2xl md:text-4xl lg:text-[42px] text-graphite font-light max-w-xl mx-auto leading-[1.25] transition-all duration-1000 delay-300 ease-out ${
              headerVisible
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-5'
            }`}
          >
            Por que escolher a{' '}
            <span className="italic">Dra. Nathália</span>?
          </h2>
          <p
            className={`font-body text-sm md:text-base text-graphite/40 mt-5 max-w-md mx-auto leading-[1.7] font-light transition-all duration-1000 delay-500 ease-out ${
              headerVisible
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-4'
            }`}
          >
            Uma experiência de cuidado pensada para quem busca excelência,
            discrição e resultados.
          </p>
        </div>

        {/* Editorial Card Grid — 3 columns desktop, 1 column mobile */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {differentials.map((item, index) => {
            const isVisible = visibleCards[index];
            return (
              <div
                key={item.id}
                ref={(el) => { cardsRef.current[index] = el; }}
                className={`group relative overflow-hidden transition-all duration-700 ease-out cursor-default ${
                  isVisible
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-10'
                }`}
              >
                {/* Card body */}
                <div className="relative h-full bg-white/25 backdrop-blur-sm border border-gold-matte/[0.06] hover:border-gold-matte/20 hover:bg-white/40 transition-all duration-500 ease-out p-8 md:p-9 lg:p-10 min-h-[260px] md:min-h-[300px] flex flex-col">
                  {/* Top accent line — gold, subtle, grows on hover */}
                  <div className="absolute top-0 left-0 right-0 h-[2px] overflow-hidden">
                    <div className="h-full w-0 group-hover:w-full bg-gold-matte/25 transition-all duration-700 ease-out" />
                  </div>

                  {/* Watermark number */}
                  <span className="font-heading text-[56px] md:text-[64px] leading-none text-gold-matte/[0.07] select-none absolute top-4 right-5 md:right-6">
                    {item.number}
                  </span>

                  {/* Content */}
                  <div className="relative z-10 flex flex-col h-full">
                    {/* Thin separator */}
                    <div className="w-10 h-[1px] bg-gold-matte/20 mb-5" />

                    {/* Title */}
                    <h3 className="font-heading text-base md:text-lg text-graphite font-light leading-[1.35] mb-4">
                      {item.title}
                    </h3>

                    {/* Description */}
                    <p className="font-body text-[13px] md:text-sm text-graphite/45 leading-[1.75] font-light flex-1">
                      {item.desc}
                    </p>

                    {/* Bottom micro-indicator */}
                    <div className="mt-auto pt-6 flex items-center gap-2">
                      <span className="font-body text-[10px] tracking-[0.2em] uppercase text-graphite/25 group-hover:text-gold-matte/40 transition-colors duration-500">
                        {item.number}
                      </span>
                      <div className="flex-1 h-[1px] bg-gold-matte/10 group-hover:bg-gold-matte/20 transition-colors duration-500" />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom editorial line */}
        <div
          className={`mt-16 md:mt-24 flex items-center justify-center gap-4 transition-all duration-1000 delay-[800ms] ease-out ${
            headerVisible
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-4'
          }`}
        >
          <div className="w-16 md:w-24 h-[1px] bg-gold-matte/15" />
          <div className="w-2 h-2 rounded-full border border-gold-matte/20" />
          <div className="w-16 md:w-24 h-[1px] bg-gold-matte/15" />
        </div>
      </div>
    </section>
  );
}